/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useState, useEffect } from "react";
import { When } from "react-if";
import {
  Box,
  Button,
  FlexContainer,
  Message,
  Spacer,
  Text,
  theme,
} from "@nordcloud/gnui";
import {
  CloudWasteSummaryCategorisedDocument,
  useCloudWasteSettingsCategorisedQuery,
  useUpdateCloudWasteSettingMutation,
} from "~/generated/graphql";
import {
  BreadcrumbsBox,
  GlobalSpinner,
  UniversalWrap,
  TableLoader,
} from "~/components";
import { ACTIONS, Can, useAccess } from "~/services/auth";
import { useBillingPeriod } from "~/services/customers";
import { showError, showSuccess } from "~/services/toast";
import { CloudWasteSettingsForm } from "./CloudWasteSettingsForm";
import {
  CLOUD_WASTE_DESCRIPTION,
  ERROR_MESSAGE,
  SAVINGS_SUGGESTIONS_INFO,
  SUCCESS_MESSAGE,
} from "./constants";
import { UpdatedSettings } from "./types";
import { isSettingsRecalculated, returnDiff, setLastChangeInfo } from "./utils";

export function CloudWaste() {
  const [key, setKey] = useState(0);
  const [hasChanges, setHasChanges] = useState(false);
  const [showRecalculationInfo, setShowRecalculationInfo] = useState(false);
  const { isPreviousBillingPeriod, isCurrentBillingPeriod } =
    useBillingPeriod();

  const { data, loading, error, refetch } =
    useCloudWasteSettingsCategorisedQuery({
      // cache has to be disabled due to id duplication in setting parameters (the values are being overwritten)
      fetchPolicy: "no-cache",
    });

  const [updateSetting, { loading: isUpdateSettingLoading }] =
    useUpdateCloudWasteSettingMutation({
      refetchQueries: [{ query: CloudWasteSummaryCategorisedDocument }],
      onError: (updateSettingError) => {
        showError(`${updateSettingError.message}`);
      },
    });

  const { hasAccess: canUpdateSavingSuggestions } = useAccess(
    ACTIONS.updateSavingsSuggestions
  );

  const settings = data?.cloudWasteSettingsCategorised ?? [];

  const handleSubmit = async (formData: UpdatedSettings) => {
    setHasChanges(false);
    const settingsToUpdate = returnDiff(
      settings.flatMap((category) => category.settings),
      formData
    );

    const requests = settingsToUpdate.map((setting) => {
      return updateSetting({
        variables: {
          input: setting,
        },
      });
    });

    try {
      await Promise.all(requests);
      showSuccess(SUCCESS_MESSAGE);
      await refetch();
      setLastChangeInfo();
    } catch {
      showError(ERROR_MESSAGE);
      setHasChanges(true);
    }
  };

  const resetForm = () => {
    // reset form state, clear user input to initial values
    setKey((prevState) => prevState + 1);
    setHasChanges(false);
  };

  const isSaveDisabled = !hasChanges;

  useEffect(() => {
    setShowRecalculationInfo(isSettingsRecalculated());
  }, [data]);

  const isCloudWasteSettingsFormDisabled =
    isPreviousBillingPeriod || !canUpdateSavingSuggestions;

  const showSavingsSuggestionsInfo =
    isCurrentBillingPeriod && showRecalculationInfo;

  return (
    <>
      <BreadcrumbsBox title="Savings Suggestions Settings">
        <Can action={ACTIONS.updateSavingsSuggestions}>
          <FlexContainer justifyContent="flex-end">
            <When condition={isCurrentBillingPeriod}>
              {hasChanges && (
                <Button
                  severity="low"
                  icon="cancel"
                  marginRight={theme.spacing.spacing04}
                  onClick={resetForm}
                >
                  Cancel
                </Button>
              )}
              <Button
                severity={hasChanges ? "high" : "medium"}
                icon="save"
                form="cloud-waste-settings"
                disabled={isSaveDisabled}
                css={{
                  border: hasChanges ? 0 : "1px solid currentColor",
                }}
              >
                Save Settings
              </Button>
            </When>
          </FlexContainer>
        </Can>
      </BreadcrumbsBox>
      <Box spacing="spacing04">
        <div css={{ marginBottom: theme.spacing.spacing03 }}>
          <When condition={showSavingsSuggestionsInfo}>
            <Message status="notification" image="info">
              <FlexContainer
                direction="column"
                alignItems="flex-start"
                css={{ width: "75%" }}
              >
                <Text
                  mb={theme.spacing.spacing01}
                  weight="medium"
                  color={theme.color.text.info}
                >
                  {SAVINGS_SUGGESTIONS_INFO}
                </Text>
              </FlexContainer>
            </Message>
            <Spacer height={theme.spacing.spacing02} />
          </When>
          <Message status="notification" image="info">
            <FlexContainer
              direction="column"
              alignItems="flex-start"
              css={{ width: "75%" }}
            >
              <Text
                mb={theme.spacing.spacing02}
                weight="medium"
                color={theme.color.text.info}
              >
                Savings Suggestions
              </Text>
              <Text
                mb={theme.spacing.spacing00}
                color={theme.color.text.text02}
              >
                {CLOUD_WASTE_DESCRIPTION}
              </Text>
            </FlexContainer>
          </Message>
        </div>
        <UniversalWrap
          errorProps={{ error }}
          loaderProps={{
            loading,
            Component: Loader,
          }}
        >
          <CloudWasteSettingsForm
            key={key}
            disabled={isCloudWasteSettingsFormDisabled}
            initialData={settings}
            onSubmit={handleSubmit}
            onChange={() => setHasChanges(true)}
          />
          <GlobalSpinner loading={isUpdateSettingLoading} />
        </UniversalWrap>
      </Box>
    </>
  );
}

function Loader() {
  return (
    <>
      <TableLoader
        gapY={10}
        gapX={0}
        css={{
          marginTop: theme.spacing.spacing08,
        }}
        rows={[
          {
            count: 2,
            height: 140,
            items: [
              {
                widthPercent: 100,
              },
            ],
          },
          {
            count: 1,
            height: 70,
            items: [
              {
                widthPercent: 100,
              },
            ],
          },
        ]}
      />
      <TableLoader
        gapY={10}
        gapX={0}
        css={{
          marginTop: theme.spacing.spacing08,
        }}
        rows={[
          {
            count: 2,
            height: 70,
            items: [
              {
                widthPercent: 100,
              },
            ],
          },
          {
            count: 1,
            height: 140,
            items: [
              {
                widthPercent: 100,
              },
            ],
          },
        ]}
      />
      <TableLoader
        gapY={10}
        gapX={0}
        css={{
          marginTop: theme.spacing.spacing08,
        }}
        rows={[
          {
            count: 1,
            height: 70,
            items: [
              {
                widthPercent: 100,
              },
            ],
          },
          {
            count: 3,
            height: 140,
            items: [
              {
                widthPercent: 100,
              },
            ],
          },
        ]}
      />
    </>
  );
}
